import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import styles from './PodcastPage.module.css';

const PodcastPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [commentsModalOpen, setCommentsModalOpen] = useState(false);
  const [selectedPodcast, setSelectedPodcast] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');

  useEffect(() => {
    const fetchPodcasts = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await apiGet('/podcast/get-all-podcasts');
        
        if (response.ok) {
          const podcastList = await response.json();
          setPodcasts(podcastList);
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to load podcasts');
        }
      } catch (error) {
        console.error('Error fetching podcasts:', error);
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchPodcasts();
  }, []);

  const filteredPodcasts = podcasts.filter(podcast =>
    podcast.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Comments functionality
  const fetchComments = async (podcastId) => {
    setCommentsLoading(true);
    try {
      const response = await apiGet(`/podcast/${podcastId}/comments`);
      if (response.ok) {
        const commentsData = await response.json();
        // Sort comments by created_at in descending order (most recent first)
        const sortedComments = (commentsData || []).sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setComments(sortedComments);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleViewComments = (podcast) => {
    setSelectedPodcast(podcast);
    setCommentsModalOpen(true);
    fetchComments(podcast.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPodcast) return;
    
    try {
      const response = await apiPost(`/podcast/${selectedPodcast.id}/comments`, {
        content: newComment.trim()
      });
      
      if (response.ok) {
        setNewComment('');
        fetchComments(selectedPodcast.id); // Refresh comments
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const handleEditComment = (comment) => {
    setEditingComment(comment.id);
    setEditCommentText(comment.content);
  };

  const handleSaveEdit = async (commentId) => {
    if (!editCommentText.trim()) return;
    
    try {
      const response = await apiPut(`/comments/${commentId}`, {
        content: editCommentText.trim()
      });
      
      if (response.ok) {
        setEditingComment(null);
        setEditCommentText('');
        if (selectedPodcast) {
          fetchComments(selectedPodcast.id); // Refresh comments
        }
      }
    } catch (error) {
      console.error('Error updating comment:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      const response = await apiDelete(`/comments/${commentId}`);
      
      if (response.ok) {
        if (selectedPodcast) {
          fetchComments(selectedPodcast.id); // Refresh comments
        }
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  if (loading) {
    return (
      <div className={styles.podcastPage}>
        <h1>Podcasts</h1>
        <div className="loading">Loading podcasts...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.podcastPage}>
        <h1>Podcasts</h1>
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn btn-primary">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.podcastPage}>
      <header className={styles.podcastHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerText}>
            <h1>Your Podcasts</h1>
            <p>Browse your podcasts.</p>
          </div>
          <div className={styles.buttonWrapper}>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/create-podcast')}
            >
              <strong>Create Podcast</strong>
            </button>
          </div>
        </div>
      </header>
      
      <div className={styles.podcastGrid}>
        {filteredPodcasts.length === 0 ? (
          <div className={styles.emptyState}>
            <h3>No podcasts found</h3>
          </div>
        ) : (
          filteredPodcasts.map(podcast => (
            <div key={podcast.id} className={styles.podcastCard}>
              <div className={styles.podcastInfo}>
                <h3>{podcast.title}</h3>
                
                {podcast.prompt && (
                  <div className={styles.promptPreview}>
                    <p>{podcast.prompt.length > 200 ? `${podcast.prompt.substring(0, 200)}...` : podcast.prompt}</p>
                  </div>
                )}
                
                <div className={styles.podcastMeta}>
                  <span className={styles.frequency}>
                    <strong>Frequency:</strong> {podcast.frequency === 'none' ? 'Ad-Hoc' : podcast.frequency}
                  </span>
                  <span className={styles.createdDate}>
                    <strong>Created:</strong> {new Date(podcast.created_at).toLocaleDateString()}
                  </span>
                  <span className={`${styles.status} ${styles[podcast.status]}`}>
                    <strong>Status:</strong> 
                    {podcast.status === 'active' && <span className={styles.checkmark}>✓</span>}
                    {podcast.status === 'active' ? 'Active' : podcast.status}
                  </span>
                </div>
                
                <div className={styles.podcastActions}>
                  <Link to={`/podcast/get-podcast/${podcast.id}`} className="btn btn-primary btn-compact">
                    View Details
                  </Link>
                  <button 
                    className={styles.commentsButton}
                    onClick={() => handleViewComments(podcast)}
                    title="View Comments"
                  >
                    💬
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Comments Modal */}
      {commentsModalOpen && selectedPodcast && (
        <div className={styles.modalOverlay} onClick={() => setCommentsModalOpen(false)}>
          <div className={styles.commentsModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Comments for "{selectedPodcast.title}" ({comments.length})</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setCommentsModalOpen(false)}
                type="button"
              >
                ×
              </button>
            </div>
            
            <div className={styles.modalContent}>
              {/* Add Comment Form */}
              <div className={styles.addCommentForm}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a comment..."
                  className={styles.commentTextarea}
                  rows="3"
                />
                <button
                  onClick={handleAddComment}
                  className="btn btn-primary"
                  disabled={!newComment.trim()}
                >
                  Add Comment
                </button>
              </div>

              {/* Comments List */}
              <div className={styles.commentsList}>
                {commentsLoading ? (
                  <div className={styles.loading}>Loading comments...</div>
                ) : comments.length === 0 ? (
                  <div className={styles.emptyComments}>
                    <p>No comments yet. Be the first to comment!</p>
                  </div>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className={styles.commentCard}>
                      <div className={styles.commentHeader}>
                        <div className={styles.commentAuthor}>
                          <strong>{comment.user?.first_name} {comment.user?.last_name}</strong>
                          <span className={styles.commentDate}>
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {comment.user_id === user?.id && (
                          <div className={styles.commentActions}>
                            {editingComment === comment.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveEdit(comment.id)}
                                  className="btn btn-sm btn-primary"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="btn btn-sm btn-secondary"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEditComment(comment)}
                                  className="btn btn-sm btn-secondary"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteComment(comment.id)}
                                  className="btn btn-sm btn-danger"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div className={styles.commentContent}>
                        {editingComment === comment.id ? (
                          <textarea
                            value={editCommentText}
                            onChange={(e) => setEditCommentText(e.target.value)}
                            className={styles.editCommentTextarea}
                            rows="3"
                          />
                        ) : (
                          <p>{comment.content}</p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PodcastPage;
